/**
 * Parent packet public UI: submit gating + phone format + validation messages.
 * Phone rules extend beyond legacy structural canSubmit (Fay feedback): required slots must be
 * non-empty with exactly 10 digits after stripping non-digits; optional phones allow blank, else 10 digits.
 */
import { getPacketSiteSlug } from './NaToggle';
import {
  consentAckDeclineSatisfied,
  fa09RemoteLearningSatisfied,
  pushConsentAckDecline,
  pushRequiredConsentAck,
  requiredConsentAckSatisfied,
} from './parentPacketConsent';
import { confirmAgreedValidationMessage } from './parentPacketConfirmLabel';

/** Normalize saved fa_03 rows (legacy `name` → first/last) for UI + validation. */
export function normalizeFa03BusRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => {
    if (!r || typeof r !== 'object') {
      return { first_name: '', last_name: '', relationship: '', address: '', phone: '' };
    }
    let fn = (r.first_name || '').trim();
    let ln = (r.last_name || '').trim();
    if ((!fn || !ln) && (r.name || '').trim()) {
      const parts = (r.name || '').trim().split(/\s+/).filter(Boolean);
      if (!fn) fn = parts[0] || '';
      if (!ln) ln = parts.slice(1).join(' ') || '';
    }
    return {
      first_name: fn,
      last_name: ln,
      relationship: (r.relationship || '').trim(),
      address: (r.address || '').trim(),
      phone: (r.phone || '').trim(),
    };
  });
}

/** Rows 1–3 (indices 0–2): first + last + phone, or legacy name (2+ tokens) + phone. */
export function fa03FirstThreeRowsComplete(rows) {
  const r = rows || [];
  for (let i = 0; i < 3; i += 1) {
    const row = r[i] || {};
    const fn = (row.first_name || '').trim();
    const ln = (row.last_name || '').trim();
    const ph = (row.phone || '').trim();
    if (fn && ln && ph) continue;
    const legacy = (row.name || '').trim();
    const parts = legacy.split(/\s+/).filter(Boolean);
    if (legacy && ph && parts.length >= 2) continue;
    return false;
  }
  return true;
}

export function stripDigits(v) {
  return String(v ?? '').replace(/\D/g, '');
}

export function packetFieldId(suffix) {
  return `packet-field-${suffix}`;
}

/**
 * Yes/No keys that must be answered ('yes' or 'no') on submit for the 14th Ave
 * new-student intake forms. Unanswered ('' / undefined) blocks submit. Order
 * here drives the firstErrorId for scroll-to-error.
 */
export const FA16_REQUIRED_YESNO_KEYS = [
  'seizures_yn',
  'sleep_takes_nap',
  'sleep_through_night',
  'sleep_toilet_accidents',
  'comm_phrases',
  'comm_sentences',
  'comm_directions',
  'comm_responds_name',
  'comm_sign_language',
  'outside_unattended',
  'play_occupies_self',
  'play_with_other_children',
  'play_with_adults',
  'play_appropriately_toys',
  'seeks_attention',
  'shares_attention',
];

export const FA17_REQUIRED_YESNO_KEYS = [
  'feeding_refuses_food',
  'feeding_vomits_after',
  'feeding_vomits_during',
  'feeding_takes_long',
  'feeding_difficulty_liquids',
  'feeding_difficulty_solids',
  'feeding_food_spills',
  'feeding_cries_during',
  'feeding_cries_after',
  'feeding_needs_force_feeding',
  'feeding_drools_frequently',
  'feeding_coughs_when_eating',
  'feeding_chokes_when_eating',
];

/** fa_12 flat UI keys that must be answered yes/no before submit (paper form Y/N grid). */
export const FA12_REQUIRED_YESNO_KEYS = [
  'seizures_yn',
  'feed_refuses_food_yn',
  'feed_vomits_after_yn',
  'feed_vomits_during_yn',
  'feed_long_time_yn',
  'feed_liquids_difficulty_yn',
  'feed_solids_difficulty_yn',
  'feed_food_spills_yn',
  'feed_cries_during_yn',
  'feed_cries_after_yn',
  'feed_force_fed_yn',
  'feed_drools_yn',
  'feed_coughs_yn',
  'feed_chokes_yn',
  'naps_yn',
  'sleeps_through_yn',
  'toilet_accidents_yn',
  'phrases_yn',
  'sentences_yn',
  'directions_yn',
  'name_yn',
  'sign_yn',
  'goes_outside_yn',
  'occupies_self_yn',
  'plays_other_children_yn',
  'plays_adults_yn',
  'plays_toys_yn',
  'seek_attention_yn',
  'share_attention_yn',
];

export const FA12_YESNO_NA_ALLOWED_KEYS = new Set(['sign_yn']);

/** Core fa_12 text fields (full Fay matrix blocked on markup PDF). */
export const FA12_REQUIRED_TEXT_FIELDS = [
  ['person_completing', 'Person completing form is required'],
  ['sex', 'Sex (M/F) is required'],
  ['address', 'Address is required'],
  ['zip', 'ZIP code is required'],
  ['phone', 'Phone is required'],
  ['emergency_name', 'Emergency contact name is required'],
  ['emergency_phone', 'Emergency contact phone is required'],
  ['language_home', 'Language spoken at home is required'],
];

export function isYesNoAnswered(value) {
  return value === 'yes' || value === 'no';
}

/**
 * fa_16 Yes/No keys that ALSO accept 'na' as a satisfied answer. Currently
 * a single key: Communication → "Uses sign language" — a parent without a
 * deaf/hard-of-hearing context needs N/A as a legitimate third choice.
 * Every other required Y/N key on both forms is strict 'yes' | 'no'.
 */
export const FA16_YESNO_NA_ALLOWED_KEYS = new Set(['comm_sign_language']);

export function isRequiredYesNoSatisfied(key, value) {
  if (FA16_YESNO_NA_ALLOWED_KEYS.has(key) || FA12_YESNO_NA_ALLOWED_KEYS.has(key)) {
    return value === 'yes' || value === 'no' || value === 'na';
  }
  return isYesNoAnswered(value);
}

/** form_10 (Child Identification): required text fields; nickname optional. */
export const FORM10_REQUIRED_TEXT_FIELDS = [
  'hair_color',
  'eye_color',
  'glasses',
  'physical_description',
  'usual_behavior',
  'communication_ability',
  'favorite_locations',
];

const FORM10_REQUIRED_TEXT_MESSAGES = {
  hair_color: "Please enter your child's hair color.",
  eye_color: "Please enter your child's eye color.",
  glasses: "Please indicate whether your child wears glasses.",
  physical_description: "Please provide your child's physical description.",
  usual_behavior: "Please describe your child's usual behavior.",
  communication_ability: "Please describe your child's communication ability.",
  favorite_locations: "Please list your child's favorite locations.",
};

export const FA08_AFFIRM_KEYS = [
  'consent_video',
  'consent_video_behind',
  'consent_audio',
  'consent_photo',
  'consent_photo_blurred',
  'consent_social_media',
];

export function fa08HasAffirmative(fields) {
  return FA08_AFFIRM_KEYS.some((k) => !!fields[k]);
}

export function isNonBlankText(value) {
  return String(value ?? '').trim().length > 0;
}

function form07RequiredFieldsValid(fields, { showMother = true } = {}) {
  if (showMother && !isNonBlankText(fields.mother_name)) return false;
  if (showMother && (!isNonBlankText(fields.mother_cell) || stripDigits(fields.mother_cell).length !== 10)) {
    return false;
  }
  if (!isNonBlankText(fields.ec1_name)) return false;
  if (!isNonBlankText(fields.ec1_phone) || stripDigits(fields.ec1_phone).length !== 10) return false;
  if (!isNonBlankText(fields.ec2_name)) return false;
  if (!isNonBlankText(fields.ec2_phone) || stripDigits(fields.ec2_phone).length !== 10) return false;
  return true;
}

function pushRequiredPhone(errors, fieldErrorMap, raw, label, fieldKey) {
  const id = packetFieldId(fieldKey);
  const t = String(raw ?? '').trim();
  if (!t) {
    const msg = `${label} is required`;
    errors.push({ id, fieldKey, message: msg });
    fieldErrorMap[fieldKey] = msg;
    return;
  }
  const d = stripDigits(raw);
  if (d.length !== 10) {
    const msg = `${label} must be 10 digits`;
    errors.push({ id, fieldKey, message: msg });
    fieldErrorMap[fieldKey] = msg;
  }
}

export function form10RequiredTextValid(fields) {
  return FORM10_REQUIRED_TEXT_FIELDS.every((key) => isNonBlankText(fields[key]));
}

/**
 * Structural submit readiness only (matches legacy canSubmit; no phone digit count).
 */
const BS08_CONSENT_KEYS = [
  'consent_video',
  'consent_audio',
  'consent_photo',
  'consent_school_use',
  'consent_website',
];
const BS08_CONSENT_REQUIRED_MESSAGE = 'Select at least one photo/video consent option, or choose I DO NOT consent';

function bs08HasAffirmative(fields) {
  return BS08_CONSENT_KEYS.some((k) => !!fields[k]);
}

function bs08ConsentSatisfied(fields) {
  const anyAffirm = bs08HasAffirmative(fields);
  if (anyAffirm && fields.consent_none) return false;
  return anyAffirm || !!fields.consent_none;
}

export function submitFieldsValid(formId, fields, packet = null) {
  if (!formId) return true;
  if (formId === 'form_16' || formId === 'form_17') return true;
  if (formId === 'bs_08') {
    return !!fields.confirm_agreed && bs08ConsentSatisfied(fields);
  }
  if (formId === 'form_11' || formId === 'form_12') {
    if (formId === 'form_12' && !consentAckDeclineSatisfied(fields, 'creams_consent_ack', 'creams_consent_decline')) {
      return false;
    }
    return !!fields.confirm_agreed;
  }
  if (formId === 'form_13') {
    return !!fields.confirm_agreed;
  }
  if (formId === 'form_15') {
    return requiredConsentAckSatisfied(fields, 'release_consent_ack')
      && !!fields.confirm_agreed;
  }
  if (formId === 'form_18') {
    return !!fields.confirm_policy && !!fields.confirm_agreed;
  }
  if (formId === 'form_19') {
    return !!fields.handbook_received_ack
      && !!fields.handbook_illness_policy_ack
      && !!fields.handbook_therapy_makeup_ack;
  }
  if (typeof formId === 'string' && formId.startsWith('fa_')) {
    if (formId === 'fa_03') {
      const rows = normalizeFa03BusRows(fields.bus_pickup_rows || []);
      const baseOk = !!fields.confirm_agreed && fa03FirstThreeRowsComplete(rows);
      if (!baseOk) return false;
      if (getPacketSiteSlug(packet) === '55th-street') {
        if (!isNonBlankText(fields.pickup_address)) return false;
        if (!isNonBlankText(fields.dropoff_address)) return false;
        if (!isNonBlankText(fields.pickup_phone)) return false;
        if (stripDigits(fields.pickup_phone).length !== 10) return false;
        if (!isNonBlankText(fields.dropoff_phone)) return false;
        if (stripDigits(fields.dropoff_phone).length !== 10) return false;
      }
      return true;
    }
    if (formId === 'fa_04') {
      const baseOk = !!fields.confirm_agreed
        && isNonBlankText(fields.allergies)
        && requiredConsentAckSatisfied(fields, 'emergency_treatment_ack');
      if (!baseOk) return false;
      if (getPacketSiteSlug(packet) === '55th-street') {
        return ['ec1_phone', 'ec2_phone', 'ec3_phone'].every(
          (k) => stripDigits(fields[k]).length === 10,
        );
      }
      return true;
    }
    if (formId === 'fa_05') {
      return !!fields.confirm_agreed
        && consentAckDeclineSatisfied(fields, 'otc_consent_ack', 'otc_consent_decline');
    }
    if (formId === 'fa_06') return !!fields.confirm_agreed && !!fields.understands_labeled_bottles;
    if (formId === 'fa_07') {
      return !!fields.confirm_agreed
        && requiredConsentAckSatisfied(fields, 'doctor_comm_ack')
        && requiredConsentAckSatisfied(fields, 'release_info_ack');
    }
    if (formId === 'fa_08') {
      const anyAffirm = fa08HasAffirmative(fields);
      if (anyAffirm && fields.consent_none) return false;
      return !!fields.confirm_agreed && (anyAffirm || !!fields.consent_none);
    }
    if (formId === 'fa_09') {
      return !!fields.confirm_agreed && fa09RemoteLearningSatisfied(fields);
    }
    if (formId === 'fa_10') {
      return !!fields.confirm_agreed
        && consentAckDeclineSatisfied(fields, 'trips_ack', 'trips_decline');
    }
    if (formId === 'fa_12') {
      if (!fields.confirm_agreed) return false;
      if (!FA12_REQUIRED_YESNO_KEYS.every((k) => isRequiredYesNoSatisfied(k, fields[k]))) return false;
      return FA12_REQUIRED_TEXT_FIELDS.every(([key]) => isNonBlankText(fields[key]));
    }
    if (formId === 'fa_16') {
      if (!fields.confirm_agreed) return false;
      return FA16_REQUIRED_YESNO_KEYS.every((k) => isRequiredYesNoSatisfied(k, fields[k]));
    }
    if (formId === 'fa_17') {
      if (!fields.confirm_agreed) return false;
      return FA17_REQUIRED_YESNO_KEYS.every((k) => isRequiredYesNoSatisfied(k, fields[k]));
    }
    return !!fields.confirm_agreed;
  }
  if (formId === 'form_10') {
    return form10RequiredTextValid(fields) && !!fields.confirm_agreed;
  }
  if (formId === 'form_07') {
    const p = packet || {};
    const showMother = p.has_mother !== false;
    return !!fields.confirm_agreed && form07RequiredFieldsValid(fields, { showMother });
  }
  if (formId === 'wm_03') {
    return !!fields.confirm_agreed
      && isNonBlankText(fields.ec1_name)
      && isNonBlankText(fields.ec1_cell_phone);
  }
  if (formId === 'wm_07') {
    return !!fields.confirm_agreed
      && (fields.no_medications || isNonBlankText(fields.medication_name));
  }
  if (formId === 'wm_10') {
    return !!fields.confirm_agreed
      && isNonBlankText(fields.home_address)
      && isNonBlankText(fields.home_city)
      && isNonBlankText(fields.home_zip)
      && isNonBlankText(fields.school_district)
      && isNonBlankText(fields.mother_name)
      && isNonBlankText(fields.mother_cell)
      && isNonBlankText(fields.father_name)
      && isNonBlankText(fields.father_cell)
      && isNonBlankText(fields.auth1_name)
      && isNonBlankText(fields.auth1_cell);
  }
  if (formId === 'wm_12') {
    return !!fields.confirm_agreed && isNonBlankText(fields.alert_description);
  }
  if (formId === 'wm_09' || formId === 'wm_13') {
    return !!fields.confirm_agreed;
  }
  if (typeof formId === 'string' && formId.startsWith('wm_')) {
    return !!fields.confirm_agreed;
  }
  return !!fields.confirm_agreed;
}

function pushPhoneOptional(errors, fieldErrorMap, raw, label, fieldKey) {
  const id = packetFieldId(fieldKey);
  const t = String(raw ?? '').trim();
  if (!t) return;
  const d = stripDigits(raw);
  if (d.length !== 10) {
    const msg = `${label} must be 10 digits`;
    errors.push({ id, fieldKey, message: msg });
    fieldErrorMap[fieldKey] = msg;
  }
}

/**
 * Full validation: structural + explicit messages + phone digit rules.
 * @returns {{ ok: boolean, errors: Array<{id: string, fieldKey: string, message: string}>, fieldErrorMap: Record<string, string>, firstErrorId: string|null }}
 */
export function validateForSubmit(formId, fields, packet) {
  const errors = [];
  const fieldErrorMap = {};
  const p = packet || {};
  const showMother = p.has_mother !== false;
  const showFather = p.has_father !== false;
  const showGuardian = p.has_guardian === true;

  const pushStructural = (fieldKey, message) => {
    const id = packetFieldId(fieldKey);
    errors.push({ id, fieldKey, message });
    fieldErrorMap[fieldKey] = message;
  };

  const fa03Key = (i, part) => `fa03_bus_${i}_${part}`;

  if (formId === 'fa_03') {
    const rows = normalizeFa03BusRows(fields.bus_pickup_rows || []);
    for (let i = 0; i < 3; i += 1) {
      const row = rows[i] || {};
      const fn = (row.first_name || '').trim();
      const ln = (row.last_name || '').trim();
      const legacy = (row.name || '').trim();
      const parts = legacy.split(/\s+/).filter(Boolean);
      const legacyOk = legacy && parts.length >= 2;
      if (!fn && !legacyOk) {
        pushStructural(fa03Key(i, 'first'), `First name for Authorized person ${i + 1} is required`);
      }
      if (!ln && !legacyOk) {
        pushStructural(fa03Key(i, 'last'), `Last name for Authorized person ${i + 1} is required`);
      }
      const ph = (row.phone || '').trim();
      if (!ph) {
        pushStructural(fa03Key(i, 'phone'), `Phone for Authorized person ${i + 1} is required`);
      } else if (stripDigits(ph).length !== 10) {
        pushStructural(fa03Key(i, 'phone'), `Phone for Authorized person ${i + 1} must be 10 digits`);
      }
    }
    for (let i = 3; i < 5; i += 1) {
      const row = rows[i] || {};
      pushPhoneOptional(
        errors,
        fieldErrorMap,
        row.phone,
        `Phone for Authorized person ${i + 1}`,
        fa03Key(i, 'phone'),
      );
    }
    if (getPacketSiteSlug(packet) === '55th-street') {
      if (!isNonBlankText(fields.pickup_address)) {
        pushStructural('pickup_address', 'Pick-up address is required');
      }
      if (!isNonBlankText(fields.dropoff_address)) {
        pushStructural('dropoff_address', 'Drop-off address is required');
      }
      pushRequiredPhone(errors, fieldErrorMap, fields.pickup_phone, 'Telephone', 'pickup_phone');
      pushRequiredPhone(errors, fieldErrorMap, fields.dropoff_phone, 'Drop-off telephone', 'dropoff_phone');
    }
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'fa_04') {
    if (showMother) {
      pushPhoneOptional(errors, fieldErrorMap, fields.mother_cell, 'Mother cell', 'mother_cell');
    }
    if (showFather) {
      pushPhoneOptional(errors, fieldErrorMap, fields.father_cell, 'Father cell', 'father_cell');
    }
    if (showGuardian) {
      pushPhoneOptional(errors, fieldErrorMap, fields.guardian_cell, 'Guardian cell', 'guardian_cell');
      pushPhoneOptional(errors, fieldErrorMap, fields.guardian_work_phone, 'Guardian work phone', 'guardian_work_phone');
      pushPhoneOptional(errors, fieldErrorMap, fields.guardian_home_phone, 'Guardian home phone', 'guardian_home_phone');
    }
    const ecPhonePush = getPacketSiteSlug(packet) === '55th-street' ? pushRequiredPhone : pushPhoneOptional;
    ecPhonePush(errors, fieldErrorMap, fields.ec1_phone, 'Emergency contact 1 phone', 'ec1_phone');
    ecPhonePush(errors, fieldErrorMap, fields.ec2_phone, 'Emergency contact 2 phone', 'ec2_phone');
    ecPhonePush(errors, fieldErrorMap, fields.ec3_phone, 'Emergency contact 3 phone', 'ec3_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.pediatrician_phone, 'Pediatrician phone', 'pediatrician_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.dentist_phone, 'Dentist phone', 'dentist_phone');
    if (!isNonBlankText(fields.allergies)) {
      pushStructural(
        'allergies',
        "Please list your child's allergies or enter None if there are no known allergies",
      );
    }
    pushRequiredConsentAck(
      errors,
      fieldErrorMap,
      fields,
      'emergency_treatment_ack',
      'Emergency medical treatment authorization',
    );
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'fa_05') {
    pushPhoneOptional(errors, fieldErrorMap, fields.parent_phone, 'Primary phone', 'parent_phone');
    if (showMother) pushPhoneOptional(errors, fieldErrorMap, fields.mother_cell, 'Mother cell', 'mother_cell');
    if (showFather) pushPhoneOptional(errors, fieldErrorMap, fields.father_cell, 'Father cell', 'father_cell');
    if (showGuardian) {
      pushPhoneOptional(errors, fieldErrorMap, fields.guardian_cell, 'Guardian cell', 'guardian_cell');
      pushPhoneOptional(errors, fieldErrorMap, fields.guardian_work_phone, 'Guardian work phone', 'guardian_work_phone');
      pushPhoneOptional(errors, fieldErrorMap, fields.guardian_home_phone, 'Guardian home phone', 'guardian_home_phone');
    }
    pushPhoneOptional(errors, fieldErrorMap, fields.ec1_phone, 'Emergency contact 1 phone', 'ec1_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.ec2_phone, 'Emergency contact 2 phone', 'ec2_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.ec3_phone, 'Emergency contact 3 phone', 'ec3_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.pediatrician_phone, 'Pediatrician phone', 'pediatrician_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.dentist_phone, 'Dentist phone', 'dentist_phone');
    pushConsentAckDecline(
      errors,
      fieldErrorMap,
      fields,
      'otc_consent_ack',
      'otc_consent_decline',
      'OTC medication consent',
    );
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'fa_07') {
    pushPhoneOptional(errors, fieldErrorMap, fields.pediatrician_phone, 'Pediatrician phone', 'pediatrician_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.dentist_phone, 'Dentist phone', 'dentist_phone');
    pushRequiredConsentAck(
      errors,
      fieldErrorMap,
      fields,
      'doctor_comm_ack',
      'Communication with doctors',
    );
    pushRequiredConsentAck(
      errors,
      fieldErrorMap,
      fields,
      'release_info_ack',
      'Release of information',
    );
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'fa_09') {
    if (fields.remote_learning_decline) {
      const anyService = [
        'remote_classroom',
        'remote_speech',
        'remote_ot',
        'remote_pt',
        'remote_counseling',
      ].some((k) => !!fields[k]);
      if (anyService) {
        pushStructural(
          'remote_learning_decline',
          'Choose specific services or decline all — not both',
        );
      }
    } else if (!fa09RemoteLearningSatisfied(fields)) {
      pushStructural(
        'remote_classroom',
        'Select at least one remote service, or check "I do not consent"',
      );
    }
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'fa_10') {
    pushConsentAckDecline(
      errors,
      fieldErrorMap,
      fields,
      'trips_ack',
      'trips_decline',
      'school trips and neighborhood walks',
    );
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'fa_11') {
    pushPhoneOptional(errors, fieldErrorMap, fields.sc_phone, 'Service coordinator telephone', 'sc_phone');
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'form_07') {
    if (showMother) {
      if (!isNonBlankText(fields.mother_name)) {
        pushStructural('mother_name', 'Mother name is required');
      }
      pushRequiredPhone(errors, fieldErrorMap, fields.mother_cell, 'Mother cell', 'mother_cell');
      pushPhoneOptional(errors, fieldErrorMap, fields.mother_home_phone, 'Mother home phone', 'mother_home_phone');
    }
    if (showFather) {
      pushPhoneOptional(errors, fieldErrorMap, fields.father_cell, 'Father cell', 'father_cell');
      pushPhoneOptional(errors, fieldErrorMap, fields.father_home_phone, 'Father home phone', 'father_home_phone');
    }
    if (showGuardian) {
      pushPhoneOptional(errors, fieldErrorMap, fields.guardian_cell, 'Guardian cell', 'guardian_cell');
      pushPhoneOptional(errors, fieldErrorMap, fields.guardian_work_phone, 'Guardian work phone', 'guardian_work_phone');
      pushPhoneOptional(errors, fieldErrorMap, fields.guardian_home_phone, 'Guardian home phone', 'guardian_home_phone');
    }
    if (!isNonBlankText(fields.ec1_name)) {
      pushStructural('ec1_name', 'Emergency contact 1 name is required');
    }
    pushRequiredPhone(errors, fieldErrorMap, fields.ec1_phone, 'Emergency contact 1 phone', 'ec1_phone');
    if (!isNonBlankText(fields.ec2_name)) {
      pushStructural('ec2_name', 'Emergency contact 2 name is required');
    }
    pushRequiredPhone(errors, fieldErrorMap, fields.ec2_phone, 'Emergency contact 2 phone', 'ec2_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.pediatrician_phone, 'Pediatrician phone', 'pediatrician_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.dentist_phone, 'Dentist phone', 'dentist_phone');
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'form_14') {
    pushPhoneOptional(errors, fieldErrorMap, fields.p1_phone, 'Authorized pickup 1 phone', 'p1_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.p2_phone, 'Authorized pickup 2 phone', 'p2_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.p3_phone, 'Authorized pickup 3 phone', 'p3_phone');
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'form_15') {
    pushRequiredConsentAck(
      errors,
      fieldErrorMap,
      fields,
      'release_consent_ack',
      'Medical and educational records release',
    );
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', confirmAgreedValidationMessage(formId));
    }
  }

  if (formId === 'form_12') {
    pushConsentAckDecline(
      errors,
      fieldErrorMap,
      fields,
      'creams_consent_ack',
      'creams_consent_decline',
      'use of creams and lotions',
    );
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', confirmAgreedValidationMessage(formId));
    }
  }

  if (formId === 'form_13') {
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', confirmAgreedValidationMessage(formId));
    }
  }

  if (formId === 'form_18') {
    if (!fields.confirm_policy) {
      pushStructural('confirm_policy', 'Illness policy acknowledgment is required');
    }
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', confirmAgreedValidationMessage(formId));
    }
  }

  if (formId === 'form_19') {
    const form19Required = [
      ['handbook_received_ack', 'Handbook receipt acknowledgment'],
      ['handbook_illness_policy_ack', 'Illness Policy acknowledgment'],
      ['handbook_therapy_makeup_ack', 'Therapy makeup acknowledgment'],
    ];
    form19Required.forEach(([key, label]) => {
      if (!fields[key]) {
        pushStructural(key, `${label} is required`);
      }
    });
  }

  if (formId === 'fa_06') {
    if (!fields.understands_labeled_bottles) {
      pushStructural('understands_labeled_bottles', 'Medication bottle policy acknowledgment is required');
    }
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'bs_08') {
    const anyAffirm = bs08HasAffirmative(fields);
    if (anyAffirm && fields.consent_none) {
      pushStructural(
        'consent_none',
        'Choose consent options or "I DO NOT consent" — you cannot select both',
      );
    } else if (!anyAffirm && !fields.consent_none) {
      pushStructural('consent_video', BS08_CONSENT_REQUIRED_MESSAGE);
    }
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'fa_08') {
    const anyAffirm = fa08HasAffirmative(fields);
    if (anyAffirm && fields.consent_none) {
      pushStructural(
        'consent_none',
        'Choose consent options or "I DO NOT consent" — you cannot select both',
      );
    } else if (!anyAffirm && !fields.consent_none) {
      pushStructural('consent_none', 'Select at least one photo/video consent option, or choose I DO NOT consent');
    }
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'form_10') {
    for (const key of FORM10_REQUIRED_TEXT_FIELDS) {
      if (!isNonBlankText(fields[key])) {
        pushStructural(key, FORM10_REQUIRED_TEXT_MESSAGES[key]);
      }
    }
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (
    formId === 'fa_14'
    || formId === 'form_08'
    || formId === 'form_09'
    || formId === 'form_11'
  ) {
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', confirmAgreedValidationMessage(formId));
    }
  }

  if (formId === 'wm_03') {
    if (!isNonBlankText(fields.ec1_name)) {
      pushStructural('ec1_name', 'Emergency contact name is required');
    }
    if (!isNonBlankText(fields.ec1_cell_phone)) {
      pushStructural('ec1_cell_phone', 'Emergency contact cell phone is required');
    } else {
      pushPhoneOptional(
        errors,
        fieldErrorMap,
        fields.ec1_cell_phone,
        'Emergency contact 1 cell phone',
        'ec1_cell_phone',
      );
    }
    pushPhoneOptional(errors, fieldErrorMap, fields.ec1_home_phone, 'Emergency contact 1 home phone', 'ec1_home_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.ec2_home_phone, 'Emergency contact 2 home phone', 'ec2_home_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.ec2_cell_phone, 'Emergency contact 2 cell phone', 'ec2_cell_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.ec3_home_phone, 'Emergency contact 3 home phone', 'ec3_home_phone');
    pushPhoneOptional(errors, fieldErrorMap, fields.ec3_cell_phone, 'Emergency contact 3 cell phone', 'ec3_cell_phone');
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'wm_07') {
    if (!fields.no_medications && !isNonBlankText(fields.medication_name)) {
      pushStructural('medication_name', 'Medication name is required unless you select no medications');
    }
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'wm_10') {
    const wm10Required = [
      ['home_address', 'Home address'],
      ['home_city', 'City'],
      ['home_zip', 'ZIP code'],
      ['school_district', 'School district'],
      ['mother_name', 'Mother name'],
      ['mother_cell', 'Mother cell'],
      ['father_name', 'Father name'],
      ['father_cell', 'Father cell'],
      ['auth1_name', 'Authorized person 1 name'],
      ['auth1_cell', 'Authorized person 1 cell'],
    ];
    wm10Required.forEach(([key, label]) => {
      if (!isNonBlankText(fields[key])) {
        pushStructural(key, label + ' is required');
      }
    });
    for (let idx = 1; idx <= 4; idx += 1) {
      pushPhoneOptional(
        errors,
        fieldErrorMap,
        fields['auth' + idx + '_cell'],
        'Authorized person ' + idx + ' cell',
        'auth' + idx + '_cell',
      );
    }
    pushPhoneOptional(errors, fieldErrorMap, fields.mother_cell, 'Mother cell', 'mother_cell');
    pushPhoneOptional(errors, fieldErrorMap, fields.father_cell, 'Father cell', 'father_cell');
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'wm_12') {
    if (!isNonBlankText(fields.alert_description)) {
      pushStructural('alert_description', 'Alert description is required');
    }
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'wm_09' || formId === 'wm_13') {
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'wm_04' || formId === 'wm_06' || formId === 'wm_11') {
    if (!fields.confirm_agreed) {
      pushStructural('confirm_agreed', 'Confirm that you have read the form and your entries are accurate');
    }
  }

  if (formId === 'fa_12') {
    for (const key of FA12_REQUIRED_YESNO_KEYS) {
      if (!isRequiredYesNoSatisfied(key, fields[key])) {
        const message = FA12_YESNO_NA_ALLOWED_KEYS.has(key)
          ? 'Please answer Yes, No, or N/A'
          : 'Please answer Yes or No';
        pushStructural(key, message);
      }
    }
    for (const [key, message] of FA12_REQUIRED_TEXT_FIELDS) {
      if (!isNonBlankText(fields[key])) {
        pushStructural(key, message);
      }
    }
    if (!fields.confirm_agreed) {
      pushStructural(
        'confirm_agreed',
        'Please check the acknowledgment to continue',
      );
    }
  }

  if (formId === 'fa_16' || formId === 'fa_17') {
    const requiredYesNo = formId === 'fa_16' ? FA16_REQUIRED_YESNO_KEYS : FA17_REQUIRED_YESNO_KEYS;
    for (const key of requiredYesNo) {
      if (!isRequiredYesNoSatisfied(key, fields[key])) {
        const message = FA16_YESNO_NA_ALLOWED_KEYS.has(key)
          ? 'Please answer Yes, No, or N/A'
          : 'Please answer Yes or No';
        pushStructural(key, message);
      }
    }
    if (!fields.confirm_agreed) {
      pushStructural(
        'confirm_agreed',
        'Please check the acknowledgment to continue',
      );
    }
  }

  const ok = errors.length === 0;
  const firstErrorId = errors.length ? errors[0].id : null;
  return { ok, errors, fieldErrorMap, firstErrorId };
}

/** Caption under "Your responses" when the form includes required parent controls (incl. accuracy checkbox). */
export function shouldShowRequiredFieldsCaption(hasParentFields, informational) {
  return !!hasParentFields && !informational;
}

